import {
	DropOverInput,
	TextInput,
	DropOverInputAction,
	DropOverActionList,
	ActionListItem,
	ActionListBusyIndicator,
	ActionListInfo
} from "client/Components";
import {
	PrincipalType,
	SharePointPeoplePickerItemModel
} from "client/Api/Documents/SharePoint/sharePointSharing";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	generateTrackingId,
	setTrackingId,
	debounce
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import { useResizeObserver } from "client/Hooks";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { IInputAttachable } from "client/Forms/attachable";
import { InputValidationResult, InputValidationResultKind } from "client/Validation/input";
import { isEmailAddress } from "client_legacy/ndaw/stringFormatValidator";

export type PeoplePickerProps = {
	value: SharePointPeoplePickerItemModel | undefined;
	onChange(value: SharePointPeoplePickerItemModel | undefined): void;
	onFindMembers: (
		searchTerm: string,
		signal: AbortSignal
	) => Promise<readonly SharePointPeoplePickerItemModel[]>;
	autoFocus?: "focus" | "select";
	isInvalid?: boolean;
	areArbritraryEmailAddressesAllowed?: boolean;
	trackingId?: string;
	menuTitle: string;
};

export const PeoplePicker = (props: PeoplePickerProps): ReactElement => {
	const {
		value,
		onChange,
		onFindMembers,
		autoFocus,
		isInvalid = false,
		areArbritraryEmailAddressesAllowed = false,
		trackingId,
		menuTitle
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>(value?.name ?? "");
	const [retrievedMembers, setRetrievedMembers] = useState<readonly SharePointPeoplePickerItemModel[]>([]);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const { width } = useResizeObserver(ref);
	const lastIssuedValue: RefObject<SharePointPeoplePickerItemModel | undefined> = useRef(value);
	const findMembersCancellation: RefObject<AbortController | undefined> = useRef<AbortController>(undefined);
	const [isLoadingMembers, setIsLoadingMembers] = useState<boolean>(false);

	useEffect(
		() => {
			if (value !== lastIssuedValue.current) {
				setSearchTerm(value?.name ?? "");
			}
		},
		[value]
	);

	useEffect(
		() => findMembersCancellation.current?.abort(),
		[]
	);

	const debouncedFindMembers = useMemo(
		() => {
			return debounce(
				async (
					searchTerm: string,
					signal: AbortSignal
				): Promise<void> => {
					if (signal.aborted) {
						return;
					}

					try {
						const members: readonly SharePointPeoplePickerItemModel[] = await onFindMembers(
							searchTerm,
							signal
						);

						setRetrievedMembers(members);
						setIsLoadingMembers(false);
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}
						console.error(e);
						setIsLoadingMembers(false);
					}
				},
				400
			);
		},
		[onFindMembers]
	);

	const onChangeWrapper = useCallback(
		(value: SharePointPeoplePickerItemModel | undefined): void => {
			lastIssuedValue.current = value;
			onChange(value);
		},
		[onChange]
	);

	const searchChangeHandler = useCallback(
		(searchTerm: string): void => {
			findMembersCancellation.current?.abort();

			setIsOpen(true);
			setSearchTerm(searchTerm);
			setIsLoadingMembers(true);
			setRetrievedMembers([]);

			let newValue: SharePointPeoplePickerItemModel | undefined = undefined;
			if (areArbritraryEmailAddressesAllowed && isEmailAddress(searchTerm)) {
				newValue = {
					name: searchTerm,
					memberKey: searchTerm,
					principalType: PrincipalType.None
				};
			}

			if (!deepComparison(newValue, value)) {
				onChangeWrapper(newValue);
			}

			const cancellation = new AbortController();
			findMembersCancellation.current = cancellation;
			debouncedFindMembers(searchTerm, cancellation.signal);
		},
		[
			areArbritraryEmailAddressesAllowed,
			debouncedFindMembers,
			onChangeWrapper,
			value
		]

	);

	const actions = useMemo(
		(): readonly DropOverInputAction[] => {
			return [{
				icon: "chevron-down",
				onClick: () => setIsOpen(x => !x)
			}];
		},
		[]
	);

	const memberClickHandler = (item: SharePointPeoplePickerItemModel): void => {
		setIsOpen(false);
		setSearchTerm(item.name);
		onChangeWrapper(item);
	};

	const renderActionItems = (): ReactElement => {
		if (isLoadingMembers) {
			return <ActionListBusyIndicator trackingId={generateTrackingId(trackingId, "__busy")} />;
		}

		const membersToRender: SharePointPeoplePickerItemModel[] = [];
		if (value && retrievedMembers.findIndex(x => x === value) === -1) {
			membersToRender.push(value);
		}

		membersToRender.push(...retrievedMembers);

		if (membersToRender.length === 0) {
			return (
				<ActionListInfo
					text={localize("_NoResultsFound")}
					trackingId={generateTrackingId(trackingId, "__no-items")}
				/>
			);
		}

		return (
			<>
				{membersToRender.map((x, index) => (
					<ActionListItem
						key={index}
						text={x.name}
						onClick={() => memberClickHandler(x)}
						trackingId={generateTrackingId(trackingId, `__item|${x.principalType}__${x.memberKey}`)}
					/>
				))}
			</>
		);
	};

	return (
		<div
			{...setTrackingId(trackingId)}
			ref={ref}
		>
			<DropOverInput
				isActive={isOpen}
				actions={actions}
				isInvalid={isInvalid}
			>
				<TextInput
					value={searchTerm}
					isBorderless
					height="fill"
					onChange={searchChangeHandler}
					attachedOn="right"
					placeholder={localize("_ComboBoxPlaceholder")}
					trackingId={generateTrackingId(trackingId, "__input")}
					autoFocus={autoFocus}
				/>
			</DropOverInput>

			{isOpen && ref.current && (
				<DropOverActionList
					target={ref.current}
					onClose={() => setIsOpen(false)}
					width={width}
					isHeightConstrainted
					trackingId={generateTrackingId(trackingId, "__dropover")}
					hasResponsiveDropOver={false}
					menuTitle={menuTitle}
				>
					{renderActionItems()}
				</DropOverActionList>
			)}
		</div>
	);
};
PeoplePicker.displayName = "PeoplePicker";

export function attachToPeoplePicker(
	attachable: IInputAttachable<SharePointPeoplePickerItemModel | undefined>
): Pick<PeoplePickerProps, "value" | "onChange" | "isInvalid" | "trackingId"> {
	const {
		reducer,
		validation
	} = attachable;

	const validationState: InputValidationResult = validation?.getValidationState()
		?? { kind: InputValidationResultKind.Valid };

	return {
		value: reducer.value,
		onChange: (newValue: SharePointPeoplePickerItemModel | undefined) => {
			reducer.setValue(newValue);
			validation?.triggerInputFinished();
		},
		isInvalid: validationState.kind === InputValidationResultKind.Invalid,
		trackingId: reducer.path
	};
};
